import { useState, useCallback, useEffect } from 'react';
import type { Customer, CustomerFormData } from '../../../../modules/customer/types';
import { 
  levelConfig, 
  discountOptions, 
  quickTagSuggestions,
  defaultAvatars,
  MIN_PASSWORD_LENGTH
} from '../../../../modules/customer/constants';
import { generateRandomPassword, isValidEmail } from '../../../../modules/customer/helpers';
import { supabase } from '../../../../services/supabaseClient';
import type { IdentityDocumentSummary } from '../../../../modules/identity/types';
import { IdentityDocumentSection } from './IdentityDocumentSection';

/**
 * CustomerEditModal Props 介面
 */
interface CustomerEditModalProps {
  /** 彈窗是否開啟 */
  isOpen: boolean;
  
  /** 要編輯的客戶資料 */
  customer: Customer | null;

  /** 身分證件摘要（由 page 層面載入） */
  identitySummary?: IdentityDocumentSummary | null;
  
  /** 關閉彈窗回調 */
  onClose: () => void;
  
  /** 儲存表單回調 */
  onSave: (id: string, data: Partial<Customer>, avatarFile: File | null) => Promise<void>;
  
  /** 建立登入帳號回調 */
  onCreateAuth: (customerId: string, email: string, password: string) => Promise<void>;
  
  /** 是否正在儲存中 */
  isSaving: boolean;
  
  /** 是否正在建立登入帳號中 */
  isCreatingAuth: boolean;
  
  /** 當前 admin 角色（用於證件權限判斷） */
  adminRole?: string | null;
}

/**
 * 客戶編輯彈窗元件
 */
export function CustomerEditModal({
  isOpen,
  customer,
  identitySummary: initialIdentitySummary,
  onClose,
  onSave,
  onCreateAuth,
  isSaving,
  isCreatingAuth,
  adminRole
}: CustomerEditModalProps): JSX.Element | null {
  // ==================== 表單狀態 ====================
  
  const [form, setForm] = useState<Partial<Customer>>({});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [showAvatarPicker, setShowAvatarPicker] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<string>('');
  const [showCreateAuthModal, setShowCreateAuthModal] = useState<boolean>(false);
  const [authEmail, setAuthEmail] = useState<string>('');
  const [authPassword, setAuthPassword] = useState<string>('');
  const [showUpgradePrepaidModal, setShowUpgradePrepaidModal] = useState<boolean>(false);
  const [showCancelUrentModal, setShowCancelUrentModal] = useState<boolean>(false);
  const [cancelUrentLoading, setCancelUrentLoading] = useState<boolean>(false);
  const [urentCancelError, setUrentCancelError] = useState<string>('');
  
  // Phase 1: 身分證件狀態（mock-safe）
  const [identitySummary, setIdentitySummary] = useState<IdentityDocumentSummary | null>(initialIdentitySummary ?? null);

  // ==================== 客戶類型處理 ====================
  const customerType = (form as any).customer_type ?? 'individual';
  const isIndividualEdit = customerType === 'individual';
  const isCompanyEdit = customerType === 'company';

  // ==================== 初始化表單 ====================
  
  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name,
        company: customer.company,
        phone: customer.phone,
        email: customer.email,
        line_id: customer.line_id,
        tax_id: customer.tax_id,
        address: customer.address,
        member_level: customer.member_level,
        discount: customer.discount,
        tags: customer.tags || [],
        notes: customer.notes,
        is_urent: customer.is_urent,
        avatar: customer.avatar,
        // ── Staging: governance proposal fields ──
        customer_type: (customer as any).customer_type || 'individual',
        identity_number: (customer as any).identity_number || '',
        registered_address: (customer as any).registered_address || '',
      });
      setAvatarPreview(customer.avatar || '');
      setAuthEmail(customer.email || '');
      
      // Phase 2: 使用 page 層面傳入的 identity summary，若無則嘗試 dev fallback
      if (initialIdentitySummary) {
        setIdentitySummary(initialIdentitySummary);
      } else {
        setIdentitySummary(null);
      }
    }
  }, [customer, initialIdentitySummary]);

  // ==================== 表單更新 ====================
  
  const updateForm = useCallback((field: keyof Customer, value: any): void => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // ==================== 頭貼處理 ====================
  
  const handleAvatarUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      setShowAvatarPicker(false);
    }
  }, []);

  const selectDefaultAvatar = useCallback((url: string): void => {
    setAvatarPreview(url);
    setAvatarFile(null);
    setShowAvatarPicker(false);
  }, []);

  // ==================== 標籤管理 ====================
  
  const addTag = useCallback((): void => {
    if (newTag.trim() && !form.tags?.includes(newTag.trim())) {
      updateForm('tags', [...(form.tags || []), newTag.trim()]);
      setNewTag('');
    }
  }, [newTag, form.tags, updateForm]);

  const removeTag = useCallback((tagToRemove: string): void => {
    updateForm('tags', form.tags?.filter(tag => tag !== tagToRemove) || []);
  }, [form.tags, updateForm]);

  const addQuickTag = useCallback((tag: string): void => {
    if (!form.tags?.includes(tag)) {
      updateForm('tags', [...(form.tags || []), tag]);
    }
  }, [form.tags, updateForm]);

  // ==================== 建立登入帳號 ====================
  
  const handleOpenCreateAuth = useCallback((): void => {
    if (!form.email) {
      alert('請先輸入客戶的 Email');
      return;
    }
    setAuthEmail(form.email);
    setAuthPassword(generateRandomPassword(8));
    setShowCreateAuthModal(true);
  }, [form.email]);

  const handleCreateAuth = useCallback(async (): Promise<void> => {
    if (!customer) return;

    if (!authEmail || !isValidEmail(authEmail)) {
      alert('請輸入有效的 Email');
      return;
    }

    if (!authPassword || authPassword.length < MIN_PASSWORD_LENGTH) {
      alert(`密碼長度至少需要 ${MIN_PASSWORD_LENGTH} 個字元`);
      return;
    }

    await onCreateAuth(customer.id, authEmail, authPassword);
    setShowCreateAuthModal(false);
  }, [customer, authEmail, authPassword, onCreateAuth]);

  // ==================== 升級為儲值會員 ====================
  
  const handleUpgradeToPrepaid = useCallback((): void => {
    setShowUpgradePrepaidModal(true);
  }, []);

  const confirmUpgradeToPrepaid = useCallback(async (): Promise<void> => {
    if (!customer) return;

    try {
      // 更新會員等級為 gold（儲值會員）
      updateForm('member_level', 'gold');
      
      // 儲存變更
      await onSave(customer.id, { ...form, member_level: 'gold' }, avatarFile);
      
      setShowUpgradePrepaidModal(false);
      
      // 關閉編輯彈窗
      handleClose();
      
      alert('✅ 已成功升級為儲值會員！\n\n請前往「儲值系統」為客戶開通儲值帳戶。');
    } catch (error) {
      console.error('升級儲值會員失敗:', error);
      alert('❌ 升級失敗，請稍後再試');
    }
  }, [customer, form, avatarFile, onSave, updateForm]);

  // ==================== 取消 Urent 會員 ====================
  
  const handleCancelUrent = useCallback(async (): Promise<void> => {
    if (!customer) return;
    
    setCancelUrentLoading(true);
    setUrentCancelError('');
    
    try {
      // 檢查是否有綁定的器材正在被租借中（訂單狀態為 pending 或 approved）
      const { data: activeItems, error: checkError } = await supabase
        .from('equipment_items')
        .select(`
          id,
          serial_number,
          status
        `)
        .eq('urent_customer_id', customer.id)
        .eq('is_urent_equipment', true);
      
      if (checkError) throw checkError;
      
      // 檢查這些器材是否有進行中的訂單
      if (activeItems && activeItems.length > 0) {
        const itemIds = activeItems.map(item => item.id);
        
        // 查詢這些器材是否有 pending 或 approved 狀態的訂單
        const { data: activeOrders, error: orderError } = await supabase
          .from('order_items')
          .select(`
            equipment_item_id,
            orders!inner (
              id,
              status
            )
          `)
          .in('equipment_item_id', itemIds)
          .in('orders.status', ['pending', 'approved']);
        
        if (orderError) throw orderError;
        
        if (activeOrders && activeOrders.length > 0) {
          setUrentCancelError('此客戶有 Urent 器材正在租借中（預約或已確認狀態），無法取消 Urent 會員資格。請先完成或取消相關訂單。');
          setCancelUrentLoading(false);
          return;
        }
      }
      
      // 沒有進行中的訂單，可以取消 Urent 會員
      // 1. 解除所有綁定的器材
      if (activeItems && activeItems.length > 0) {
        const { error: unbindError } = await supabase
          .from('equipment_items')
          .update({ 
            is_urent_equipment: false, 
            urent_customer_id: null 
          })
          .eq('urent_customer_id', customer.id);
        
        if (unbindError) throw unbindError;
      }
      
      // 2. 更新客戶的 is_urent 狀態
      updateForm('is_urent', false);
      
      setShowCancelUrentModal(false);
      
      // 提示用戶需要儲存
      alert('✅ 已取消 Urent 會員資格，請點擊「儲存變更」完成操作。');
      
    } catch (error) {
      console.error('取消 Urent 會員失敗:', error);
      setUrentCancelError('取消失敗，請稍後再試');
    } finally {
      setCancelUrentLoading(false);
    }
  }, [customer, updateForm]);

  // ==================== 表單驗證與提交 ====================
  
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!customer) return;

    // 驗證必填欄位
    if (!form.name?.trim()) {
      alert('請輸入客戶姓名');
      return;
    }

    if (!form.phone?.trim() && !form.email?.trim()) {
      alert('請至少輸入電話或 Email');
      return;
    }

    if (form.email && !isValidEmail(form.email)) {
      alert('Email 格式不正確');
      return;
    }

    // 準備提交資料（使用 avatar 欄位傳遞頭貼 URL，page.tsx 會統一轉換為 avatar_url）
    // ── Staging: 移除尚未有 DB 欄位的 customer_type / identity_number / registered_address ──
    const submitData: Partial<Customer> = {
      name: form.name,
      company: form.company,
      phone: form.phone,
      email: form.email,
      line_id: form.line_id,
      tax_id: form.tax_id,
      address: form.address,
      member_level: form.member_level,
      discount: form.discount,
      tags: form.tags,
      notes: form.notes,
      is_urent: form.is_urent,
      avatar: avatarPreview || form.avatar || undefined
    };

    await onSave(customer.id, submitData, avatarFile);
  }, [customer, form, avatarPreview, avatarFile, onSave]);

  // ==================== 關閉處理 ====================
  
  const handleClose = useCallback((): void => {
    setForm({});
    setAvatarFile(null);
    setAvatarPreview('');
    setNewTag('');
    setShowAvatarPicker(false);
    setShowCreateAuthModal(false);
    setShowUpgradePrepaidModal(false);
    setShowCancelUrentModal(false);
    setUrentCancelError('');
    setIdentitySummary(null);
    onClose();
  }, [onClose]);

  // ==================== 渲染 ====================
  
  if (!isOpen || !customer) return null;

  const isPrepaidMember = form.member_level === 'gold';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col my-8">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-teal-50 to-cyan-50">
            <div>
              <h2 className="text-xl font-bold text-gray-800">編輯客戶</h2>
              <p className="text-sm text-gray-500 mt-0.5">客戶 ID: {customer.id}</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 左欄 */}
              <div className="space-y-6">
                {/* 頭貼上傳 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    客戶頭貼
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 flex items-center justify-center">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="頭貼預覽"
                          className="w-20 h-20 rounded-full object-cover border-2 border-teal-200"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                          <i className="ri-user-line text-3xl text-gray-400"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-upload-2-line mr-2"></i>
                        上傳圖片
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-image-line mr-2"></i>
                        選擇預設
                      </button>
                    </div>
                  </div>

                  {/* 預設頭貼選擇器 */}
                  {showAvatarPicker && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">選擇預設頭貼：</p>
                      <div className="grid grid-cols-6 gap-3">
                        {defaultAvatars.map((url, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectDefaultAvatar(url)}
                            className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 hover:border-teal-500 transition-colors"
                          >
                            <img src={url} alt={`預設頭貼 ${index + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 客戶類型 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    客戶類型
                  </label>
                  <select
                    value={(form as any).customer_type || 'individual'}
                    onChange={(e) => updateForm('customer_type' as any, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="individual">個人客戶</option>
                    <option value="company">公司客戶</option>
                  </select>
                </div>

                {/* 基本資訊 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    客戶姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name || ''}
                    onChange={(e) => updateForm('name', e.target.value)}
                    placeholder="請輸入客戶姓名"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* 身分證字號（個人客戶）— staging */}
                {isIndividualEdit && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      身分證字號
                      <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">Staging</span>
                    </label>
                    <input
                      type="text"
                      value={(form as any).identity_number || ''}
                      onChange={(e) => updateForm('identity_number' as any, e.target.value)}
                      placeholder="請輸入身分證字號（A123456789）"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <p className="mt-1 text-xs text-amber-600">
                      <i className="ri-information-line mr-1"></i>
                      此欄位為 staging，待 customer identity schema migration 完成後啟用
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    公司名稱
                  </label>
                  <input
                    type="text"
                    value={form.company || ''}
                    onChange={(e) => updateForm('company', e.target.value)}
                    placeholder="請輸入公司名稱"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    聯絡電話
                  </label>
                  <input
                    type="tel"
                    value={form.phone || ''}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="請輸入聯絡電話"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email || ''}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="請輸入 Email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* LINE ID */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-line-fill text-green-500 mr-1"></i>
                    LINE ID
                  </label>
                  <input
                    type="text"
                    value={form.line_id || ''}
                    onChange={(e) => updateForm('line_id', e.target.value)}
                    placeholder="請輸入客戶的 LINE ID"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    用於 LINE 通知功能，可在訂單狀態變更時發送通知
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    統一編號{isIndividualEdit ? '（公司用）' : ''}
                  </label>
                  <input
                    type="text"
                    value={form.tax_id || ''}
                    onChange={(e) => updateForm('tax_id', e.target.value)}
                    placeholder={isCompanyEdit ? "請輸入統一編號" : "公司客戶請填寫"}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    地址
                  </label>
                  <input
                    type="text"
                    value={form.address || ''}
                    onChange={(e) => updateForm('address', e.target.value)}
                    placeholder="請輸入地址"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* 戶籍地址（個人客戶）— staging */}
                {isIndividualEdit && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      戶籍地址
                      <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">Staging</span>
                    </label>
                    <input
                      type="text"
                      value={(form as any).registered_address || ''}
                      onChange={(e) => updateForm('registered_address' as any, e.target.value)}
                      placeholder="請輸入戶籍地址（若與聯絡地址不同）"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <p className="mt-1 text-xs text-amber-600">
                      <i className="ri-information-line mr-1"></i>
                      此欄位為 staging，待 customer identity schema migration 完成後啟用
                    </p>
                  </div>
                )}
              </div>

              {/* 右欄 */}
              <div className="space-y-6">
                {/* 前台登入帳號 */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">前台登入帳號</span>
                    {customer.auth_user_id ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        <i className="ri-checkbox-circle-fill mr-1"></i>
                        已建立
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        <i className="ri-close-circle-fill mr-1"></i>
                        未建立
                      </span>
                    )}
                  </div>
                  {!customer.auth_user_id && (
                    <button
                      type="button"
                      onClick={handleOpenCreateAuth}
                      disabled={isCreatingAuth}
                      className="w-full mt-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      建立登入帳號
                    </button>
                  )}
                </div>

                {/* 會員類別 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    會員類別
                  </label>
                  <select
                    value={form.member_level || 'normal'}
                    onChange={(e) => updateForm('member_level', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    {Object.entries(levelConfig).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.icon} {config.label} - {config.description}
                      </option>
                    ))}
                  </select>

                  {/* 升級為儲值會員按鈕 */}
                  {!isPrepaidMember && (
                    <button
                      type="button"
                      onClick={handleUpgradeToPrepaid}
                      className="w-full mt-3 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-sm font-medium rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                    >
                      <i className="ri-vip-crown-line mr-2"></i>
                      升級為儲值會員
                    </button>
                  )}

                  {/* 儲值會員標記 */}
                  {isPrepaidMember && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700">
                        <i className="ri-vip-crown-fill text-lg"></i>
                        <span className="text-sm font-medium">此客戶為儲值會員</span>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">
                        可前往「儲值系統」管理儲值帳戶
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    會員折扣
                  </label>
                  <select
                    value={form.discount || 100}
                    onChange={(e) => updateForm('discount', Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    {discountOptions.map((discount) => (
                      <option key={discount} value={discount}>
                        {discount}折 {discount < 100 ? `(打 ${100 - discount}% 折扣)` : '(無折扣)'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 標籤管理 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    客戶標籤
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      placeholder="輸入標籤名稱"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  {/* 已選標籤 */}
                  {form.tags && form.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {form.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 text-sm rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 rounded-full transition-colors"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 快速標籤 */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">快速新增：</p>
                    <div className="flex flex-wrap gap-2">
                      {quickTagSuggestions.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => addQuickTag(tag)}
                          disabled={form.tags?.includes(tag)}
                          className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 備註 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    備註
                  </label>
                  <textarea
                    value={form.notes || ''}
                    onChange={(e) => updateForm('notes', e.target.value)}
                    placeholder="輸入備註資訊"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  />
                </div>

                {/* Urent 客戶勾選 */}
                {customer.is_urent ? (
                  // 已是 Urent 會員 - 顯示狀態和取消按鈕
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full">
                          <i className="ri-user-star-fill text-amber-600 text-xl"></i>
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-amber-700">已是 Urent 會員</span>
                          <p className="text-xs text-amber-600 mt-0.5">此客戶已納入 Urent 管理系統</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        <i className="ri-checkbox-circle-fill mr-1"></i>
                        已開通
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setUrentCancelError('');
                        setShowCancelUrentModal(true);
                      }}
                      className="w-full mt-3 px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-close-circle-line mr-2"></i>
                      取消 Urent 會員資格
                    </button>
                  </div>
                ) : (
                  // 非 Urent 會員 - 顯示開啟勾選
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="isUrent"
                        checked={form.is_urent || false}
                        onChange={(e) => updateForm('is_urent', e.target.checked)}
                        className="w-5 h-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                      />
                      <label htmlFor="isUrent" className="cursor-pointer">
                        <span className="text-sm font-semibold text-gray-700">開通 Urent 會員</span>
                        <p className="text-xs text-gray-500 mt-0.5">勾選後此客戶將納入 Urent 管理系統</p>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Phase 2: 身分證件區塊（橫跨兩欄）── */}
            {identitySummary && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <IdentityDocumentSection
                  customerId={customer.id}
                  customerName={customer.name}
                  documents={[]}
                  summary={identitySummary}
                  adminRole={adminRole}
                  onDocumentsChange={() => {
                    // Refresh handled by parent page polling or manual close/reopen
                  }}
                />
              </div>
            )}
            {!identitySummary && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <i className="ri-question-line text-amber-500"></i>
                    <span className="text-sm text-amber-700">身分驗證狀態暫時無法確認</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-6 py-2 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {isSaving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  儲存中...
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  儲存變更
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 建立登入帳號彈窗 */}
      {showCreateAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800">建立前台登入帳號</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  登入 Email
                </label>
                <input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="請輸入 Email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  登入密碼
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    placeholder="請輸入密碼"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAuthPassword(generateRandomPassword(8))}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-2"></i>
                自動產生密碼
              </button>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowCreateAuthModal(false)}
                disabled={isCreatingAuth}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleCreateAuth}
                disabled={isCreatingAuth}
                className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                {isCreatingAuth ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    建立中...
                  </>
                ) : (
                  <>
                    <i className="ri-user-add-line"></i>
                    建立帳號
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 升級為儲值會員確認彈窗 */}
      {showUpgradePrepaidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-yellow-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-vip-crown-line text-amber-600"></i>
                升級為儲值會員
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                確定要將客戶「<span className="font-semibold text-amber-600">{customer.name}</span>」升級為儲值會員嗎？
              </p>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <i className="ri-information-line mr-1"></i>
                  升級後需前往「儲值系統」為客戶開通儲值帳戶並充值。
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowUpgradePrepaidModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={confirmUpgradeToPrepaid}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-medium rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-vip-crown-line"></i>
                確認升級
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 取消 Urent 會員確認彈窗 */}
      {showCancelUrentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-600"></i>
                取消 Urent 會員資格
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                確定要取消客戶「<span className="font-semibold text-red-600">{customer?.name}</span>」的 Urent 會員資格嗎？
              </p>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                <p className="text-sm text-amber-800">
                  <i className="ri-information-line mr-1"></i>
                  取消後，此客戶綁定的所有 Urent 器材將會解除綁定。
                </p>
              </div>
              
              {urentCancelError && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">
                    <i className="ri-error-warning-fill mr-1"></i>
                    {urentCancelError}
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowCancelUrentModal(false)}
                disabled={cancelUrentLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                返回
              </button>
              <button
                onClick={handleCancelUrent}
                disabled={cancelUrentLoading}
                className="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                {cancelUrentLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    檢查中...
                  </>
                ) : (
                  <>
                    <i className="ri-close-circle-line"></i>
                    確認取消
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}